/**
 * Domínio odontológico — notação FDI (ISO 3950), faces dentárias e catálogo de
 * condições do odontograma.
 *
 * Só é usado quando a clínica está marcada como especialidade `odontologia`.
 */

export type Dentition = 'permanent' | 'deciduous';
export type ToothFace = 'M' | 'D' | 'V' | 'L' | 'O';
export type DentalEntryStatus = 'pre_existing' | 'planned' | 'in_progress' | 'completed' | 'cancelled';

// ── Arcadas em notação FDI ───────────────────────────────────────────────────
// A ordem de cada quadrante segue a leitura da boca do paciente: do fundo
// (terceiro molar) para a linha média no lado direito, e o inverso à esquerda.

export const PERMANENT_UPPER_RIGHT = ['18', '17', '16', '15', '14', '13', '12', '11'];
export const PERMANENT_UPPER_LEFT = ['21', '22', '23', '24', '25', '26', '27', '28'];
export const PERMANENT_LOWER_RIGHT = ['48', '47', '46', '45', '44', '43', '42', '41'];
export const PERMANENT_LOWER_LEFT = ['31', '32', '33', '34', '35', '36', '37', '38'];

export const DECIDUOUS_UPPER_RIGHT = ['55', '54', '53', '52', '51'];
export const DECIDUOUS_UPPER_LEFT = ['61', '62', '63', '64', '65'];
export const DECIDUOUS_LOWER_RIGHT = ['85', '84', '83', '82', '81'];
export const DECIDUOUS_LOWER_LEFT = ['71', '72', '73', '74', '75'];

export interface DentalArchRow {
  /** Hemiarcada do lado direito do paciente (renderizada à esquerda na tela). */
  right: string[];
  /** Hemiarcada do lado esquerdo do paciente. */
  left: string[];
}

export interface DentalArchLayout {
  upper: DentalArchRow;
  lower: DentalArchRow;
}

export const PERMANENT_ARCH: DentalArchLayout = {
  upper: { right: PERMANENT_UPPER_RIGHT, left: PERMANENT_UPPER_LEFT },
  lower: { right: PERMANENT_LOWER_RIGHT, left: PERMANENT_LOWER_LEFT },
};

export const DECIDUOUS_ARCH: DentalArchLayout = {
  upper: { right: DECIDUOUS_UPPER_RIGHT, left: DECIDUOUS_UPPER_LEFT },
  lower: { right: DECIDUOUS_LOWER_RIGHT, left: DECIDUOUS_LOWER_LEFT },
};

export function getArchLayout(dentition: Dentition): DentalArchLayout {
  return dentition === 'deciduous' ? DECIDUOUS_ARCH : PERMANENT_ARCH;
}

export function getAllTeeth(dentition: Dentition): string[] {
  const arch = getArchLayout(dentition);
  return [...arch.upper.right, ...arch.upper.left, ...arch.lower.right, ...arch.lower.left];
}

export function isDeciduousTooth(toothNumber: string): boolean {
  const quadrant = Number(toothNumber.charAt(0));
  return quadrant >= 5 && quadrant <= 8;
}

export function getDentition(toothNumber: string): Dentition {
  return isDeciduousTooth(toothNumber) ? 'deciduous' : 'permanent';
}

/** Quadrantes superiores são 1, 2 (permanentes) e 5, 6 (decíduos). */
export function isUpperTooth(toothNumber: string): boolean {
  const quadrant = Number(toothNumber.charAt(0));
  return quadrant === 1 || quadrant === 2 || quadrant === 5 || quadrant === 6;
}

/**
 * Incisivos e caninos têm borda incisal; pré-molares e molares têm face oclusal.
 * A posição dentro do quadrante (segundo dígito) define o grupo.
 */
export function isAnteriorTooth(toothNumber: string): boolean {
  return Number(toothNumber.charAt(1)) <= 3;
}

export function getToothName(toothNumber: string): string {
  const position = Number(toothNumber.charAt(1));
  const deciduous = isDeciduousTooth(toothNumber);
  const names: Record<number, string> = deciduous
    ? { 1: 'Incisivo central', 2: 'Incisivo lateral', 3: 'Canino', 4: '1º molar', 5: '2º molar' }
    : {
      1: 'Incisivo central',
      2: 'Incisivo lateral',
      3: 'Canino',
      4: '1º pré-molar',
      5: '2º pré-molar',
      6: '1º molar',
      7: '2º molar',
      8: '3º molar',
    };
  const arch = isUpperTooth(toothNumber) ? 'superior' : 'inferior';
  const side = [1, 4, 5, 8].includes(Number(toothNumber.charAt(0))) ? 'direito' : 'esquerdo';
  return `${names[position] || 'Dente'} ${arch} ${side}`;
}

export const QUADRANT_LABELS: Record<string, string> = {
  '1': 'Superior direito',
  '2': 'Superior esquerdo',
  '3': 'Inferior esquerdo',
  '4': 'Inferior direito',
  '5': 'Superior direito (decíduo)',
  '6': 'Superior esquerdo (decíduo)',
  '7': 'Inferior esquerdo (decíduo)',
  '8': 'Inferior direito (decíduo)',
};

export function getQuadrantLabel(toothNumber: string): string {
  return QUADRANT_LABELS[toothNumber.charAt(0)] || '';
}

// ── Faces ────────────────────────────────────────────────────────────────────

export const TOOTH_FACES: ToothFace[] = ['V', 'M', 'O', 'D', 'L'];

/**
 * O rótulo de duas faces depende da posição do dente: a face lingual é palatina
 * na arcada superior, e a oclusal é incisal nos dentes anteriores.
 */
export function getFaceLabel(face: ToothFace, toothNumber?: string): string {
  switch (face) {
    case 'M': return 'Mesial';
    case 'D': return 'Distal';
    case 'V': return 'Vestibular';
    case 'L': return toothNumber && isUpperTooth(toothNumber) ? 'Palatina' : 'Lingual';
    case 'O': return toothNumber && isAnteriorTooth(toothNumber) ? 'Incisal' : 'Oclusal';
    default: return face;
  }
}

export function formatFaces(faces: string[] | null | undefined, toothNumber?: string): string {
  if (!faces?.length) return '—';
  return faces.map((f) => getFaceLabel(f as ToothFace, toothNumber)).join(', ');
}

// ── Catálogo de condições ────────────────────────────────────────────────────

export interface DentalConditionOption {
  code: string;
  label: string;
  /** Cor de preenchimento no odontograma. */
  color: string;
  /** `face`: exige seleção de faces. `tooth`: aplica ao dente inteiro. */
  scope: 'face' | 'tooth' | 'both';
  group: 'Lesões' | 'Restaurador' | 'Protético' | 'Cirúrgico' | 'Endodontia' | 'Periodontia' | 'Ortodontia' | 'Outros';
  /** Marca o dente como ausente no odontograma (renderiza vazado). */
  absent?: boolean;
}

export const DENTAL_CONDITIONS: DentalConditionOption[] = [
  // Lesões
  { code: 'carie', label: 'Cárie', color: '#DC2626', scope: 'face', group: 'Lesões' },
  { code: 'mancha_branca', label: 'Mancha branca', color: '#F8FAFC', scope: 'face', group: 'Lesões' },
  { code: 'fratura', label: 'Fratura', color: '#B91C1C', scope: 'both', group: 'Lesões' },
  { code: 'desgaste', label: 'Desgaste / atrição', color: '#F59E0B', scope: 'face', group: 'Lesões' },
  { code: 'abfracao', label: 'Abfração', color: '#FB923C', scope: 'face', group: 'Lesões' },
  { code: 'hipoplasia', label: 'Hipoplasia de esmalte', color: '#FDE68A', scope: 'tooth', group: 'Lesões' },
  { code: 'sensibilidade', label: 'Sensibilidade', color: '#38BDF8', scope: 'tooth', group: 'Lesões' },

  // Restaurador
  { code: 'restauracao', label: 'Restauração', color: '#2563EB', scope: 'face', group: 'Restaurador' },
  { code: 'restauracao_provisoria', label: 'Restauração provisória', color: '#93C5FD', scope: 'face', group: 'Restaurador' },
  { code: 'restauracao_insatisfatoria', label: 'Restauração insatisfatória', color: '#7C3AED', scope: 'face', group: 'Restaurador' },
  { code: 'selante', label: 'Selante', color: '#34D399', scope: 'face', group: 'Restaurador' },
  { code: 'faceta', label: 'Faceta', color: '#22D3EE', scope: 'tooth', group: 'Restaurador' },
  { code: 'nucleo', label: 'Núcleo / pino', color: '#6366F1', scope: 'tooth', group: 'Restaurador' },

  // Protético
  { code: 'coroa', label: 'Coroa', color: '#D97706', scope: 'tooth', group: 'Protético' },
  { code: 'coroa_provisoria', label: 'Coroa provisória', color: '#FCD34D', scope: 'tooth', group: 'Protético' },
  { code: 'protese_fixa', label: 'Prótese fixa / ponte', color: '#A16207', scope: 'tooth', group: 'Protético' },
  { code: 'pontico', label: 'Pôntico', color: '#CA8A04', scope: 'tooth', group: 'Protético' },
  { code: 'protese_removivel', label: 'Prótese removível', color: '#EAB308', scope: 'tooth', group: 'Protético' },

  // Cirúrgico
  { code: 'ausente', label: 'Ausente', color: '#94A3B8', scope: 'tooth', group: 'Cirúrgico', absent: true },
  { code: 'agenesia', label: 'Agenesia', color: '#CBD5E1', scope: 'tooth', group: 'Cirúrgico', absent: true },
  { code: 'extracao_indicada', label: 'Extração indicada', color: '#EF4444', scope: 'tooth', group: 'Cirúrgico' },
  { code: 'extraido', label: 'Extraído', color: '#64748B', scope: 'tooth', group: 'Cirúrgico', absent: true },
  { code: 'resto_radicular', label: 'Resto radicular', color: '#78716C', scope: 'tooth', group: 'Cirúrgico' },
  { code: 'retido_incluso', label: 'Retido / incluso', color: '#A8A29E', scope: 'tooth', group: 'Cirúrgico' },
  { code: 'implante', label: 'Implante', color: '#8B5CF6', scope: 'tooth', group: 'Cirúrgico' },

  // Endodontia
  { code: 'canal_tratado', label: 'Canal tratado', color: '#10B981', scope: 'tooth', group: 'Endodontia' },
  { code: 'canal_indicado', label: 'Canal indicado', color: '#059669', scope: 'tooth', group: 'Endodontia' },
  { code: 'retratamento', label: 'Retratamento endodôntico', color: '#047857', scope: 'tooth', group: 'Endodontia' },
  { code: 'lesao_periapical', label: 'Lesão periapical', color: '#F43F5E', scope: 'tooth', group: 'Endodontia' },

  // Periodontia
  { code: 'calculo', label: 'Cálculo', color: '#A3A3A3', scope: 'tooth', group: 'Periodontia' },
  { code: 'recessao', label: 'Recessão gengival', color: '#F472B6', scope: 'tooth', group: 'Periodontia' },
  { code: 'mobilidade', label: 'Mobilidade', color: '#E879F9', scope: 'tooth', group: 'Periodontia' },
  { code: 'bolsa_periodontal', label: 'Bolsa periodontal', color: '#C026D3', scope: 'tooth', group: 'Periodontia' },

  // Ortodontia
  { code: 'aparelho_fixo', label: 'Aparelho fixo', color: '#0EA5E9', scope: 'tooth', group: 'Ortodontia' },
  { code: 'contencao', label: 'Contenção', color: '#0284C7', scope: 'tooth', group: 'Ortodontia' },
  { code: 'giroversao', label: 'Giroversão', color: '#7DD3FC', scope: 'tooth', group: 'Ortodontia' },
  { code: 'diastema', label: 'Diastema', color: '#BAE6FD', scope: 'tooth', group: 'Ortodontia' },

  // Outros
  { code: 'em_erupcao', label: 'Em erupção', color: '#BBF7D0', scope: 'tooth', group: 'Outros' },
  { code: 'observacao', label: 'Observação', color: '#CBD5E1', scope: 'both', group: 'Outros' },
];

const CONDITION_BY_CODE = new Map(DENTAL_CONDITIONS.map((c) => [c.code, c]));

export function getCondition(code: string | null | undefined): DentalConditionOption | undefined {
  return code ? CONDITION_BY_CODE.get(code) : undefined;
}

export function getConditionLabel(code: string | null | undefined): string {
  return getCondition(code)?.label || code || '—';
}

export function getConditionColor(code: string | null | undefined): string {
  return getCondition(code)?.color || '#94A3B8';
}

export const DENTAL_CONDITION_GROUPS = Array.from(
  new Set(DENTAL_CONDITIONS.map((c) => c.group)),
);

// ── Status ───────────────────────────────────────────────────────────────────

export interface DentalStatusOption {
  value: DentalEntryStatus;
  label: string;
  color: string;
  bg: string;
}

export const DENTAL_ENTRY_STATUSES: DentalStatusOption[] = [
  { value: 'pre_existing', label: 'Pré-existente', color: '#475569', bg: '#E2E8F0' },
  { value: 'planned', label: 'A realizar', color: '#B45309', bg: '#FEF3C7' },
  { value: 'in_progress', label: 'Em andamento', color: '#1D4ED8', bg: '#DBEAFE' },
  { value: 'completed', label: 'Realizado', color: '#15803D', bg: '#DCFCE7' },
  { value: 'cancelled', label: 'Cancelado', color: '#B91C1C', bg: '#FEE2E2' },
];

const STATUS_BY_VALUE = new Map(DENTAL_ENTRY_STATUSES.map((s) => [s.value, s]));

export function getDentalStatus(value: string | null | undefined): DentalStatusOption {
  return STATUS_BY_VALUE.get(value as DentalEntryStatus) || DENTAL_ENTRY_STATUSES[1];
}

// ── Periodontograma ──────────────────────────────────────────────────────────

/** Sítios vestibulares e linguais seguem a mesma ordem mesio-distal. */
export const PERIODONTAL_SITES = ['mesial', 'central', 'distal'] as const;
export type PeriodontalSite = typeof PERIODONTAL_SITES[number];

export const PERIODONTAL_SURFACES = ['buccal', 'lingual'] as const;
export type PeriodontalSurface = typeof PERIODONTAL_SURFACES[number];

export const PERIODONTAL_SURFACE_LABELS: Record<PeriodontalSurface, string> = {
  buccal: 'Vestibular',
  lingual: 'Lingual / palatina',
};

/** Miller modificado por Miller/Lindhe — graus 0 a 3. */
export const MOBILITY_OPTIONS = [
  { value: 0, label: '0 — fisiológica' },
  { value: 1, label: 'I — até 1 mm horizontal' },
  { value: 2, label: 'II — acima de 1 mm horizontal' },
  { value: 3, label: 'III — horizontal e vertical' },
];

/** Classificação de Hamp para lesão de furca. */
export const FURCATION_OPTIONS = [
  { value: 0, label: '0 — sem lesão' },
  { value: 1, label: 'I — até 3 mm horizontal' },
  { value: 2, label: 'II — acima de 3 mm, sem atravessar' },
  { value: 3, label: 'III — atravessa a furca' },
];

/**
 * Faixas de profundidade de sondagem.
 *
 * 4 mm é separado de 5–6 mm porque é o limiar em que o sítio deixa de ser
 * mantido só com higiene e passa a exigir raspagem; ≥ 7 mm costuma indicar
 * cirurgia. Colapsar essas faixas esconde a diferença de conduta.
 */
export interface ProbingDepthTone {
  /** Cor do número. */
  text: string;
  /** Fundo suave da célula, que produz o mapa de calor da arcada. */
  background: string;
  label: string;
}

const PROBING_DEPTH_TONES: Record<'unmeasured' | 'healthy' | 'attention' | 'moderate' | 'severe', ProbingDepthTone> = {
  unmeasured: { text: '#0F172A', background: 'transparent', label: 'Não medido' },
  healthy: { text: '#15803D', background: '#F0FDF4', label: 'Até 3 mm — saudável' },
  attention: { text: '#B45309', background: '#FFFBEB', label: '4 mm — atenção' },
  moderate: { text: '#C2410C', background: '#FFF7ED', label: '5–6 mm — bolsa moderada' },
  severe: { text: '#B91C1C', background: '#FEF2F2', label: '7 mm ou mais — bolsa profunda' },
};

export function getProbingDepthTone(pd: number | null | undefined): ProbingDepthTone {
  if (pd == null) return PROBING_DEPTH_TONES.unmeasured;
  if (pd >= 7) return PROBING_DEPTH_TONES.severe;
  if (pd >= 5) return PROBING_DEPTH_TONES.moderate;
  if (pd >= 4) return PROBING_DEPTH_TONES.attention;
  return PROBING_DEPTH_TONES.healthy;
}

/** Legenda do mapa de calor de sondagem. */
export const PROBING_DEPTH_LEGEND: ProbingDepthTone[] = [
  PROBING_DEPTH_TONES.healthy,
  PROBING_DEPTH_TONES.attention,
  PROBING_DEPTH_TONES.moderate,
  PROBING_DEPTH_TONES.severe,
];

// ── Endodontia / implantes / ortodontia ──────────────────────────────────────

export const ENDODONTIC_TREATMENT_TYPES = [
  { value: 'conventional', label: 'Tratamento convencional' },
  { value: 'retreatment', label: 'Retratamento' },
  { value: 'pulpotomy', label: 'Pulpotomia' },
  { value: 'pulpectomy', label: 'Pulpectomia' },
  { value: 'apexification', label: 'Apicificação' },
  { value: 'apicectomy', label: 'Apicectomia' },
];

export const ENDODONTIC_STATUSES = [
  { value: 'planned', label: 'Planejado', color: '#B45309', bg: '#FEF3C7' },
  { value: 'in_progress', label: 'Em andamento', color: '#1D4ED8', bg: '#DBEAFE' },
  { value: 'obturated', label: 'Obturado', color: '#7C3AED', bg: '#EDE9FE' },
  { value: 'completed', label: 'Concluído', color: '#15803D', bg: '#DCFCE7' },
  { value: 'failed', label: 'Insucesso', color: '#B91C1C', bg: '#FEE2E2' },
  { value: 'cancelled', label: 'Cancelado', color: '#475569', bg: '#E2E8F0' },
];

export const PULP_DIAGNOSES = [
  'Polpa normal',
  'Pulpite reversível',
  'Pulpite irreversível sintomática',
  'Pulpite irreversível assintomática',
  'Necrose pulpar',
  'Dente previamente tratado',
  'Terapia previamente iniciada',
];

export const PERIAPICAL_DIAGNOSES = [
  'Tecidos apicais normais',
  'Periodontite apical sintomática',
  'Periodontite apical assintomática',
  'Abscesso apical agudo',
  'Abscesso apical crônico',
  'Osteíte condensante',
];

export const IMPLANT_PLATFORMS = [
  'Cone Morse',
  'Hexágono externo',
  'Hexágono interno',
  'Trilobulada',
  'Monobloco',
];

export const IMPLANT_LOADING_PROTOCOLS = [
  { value: 'immediate', label: 'Carga imediata' },
  { value: 'early', label: 'Carga precoce' },
  { value: 'conventional', label: 'Carga convencional' },
];

export const IMPLANT_STATUSES = [
  { value: 'planned', label: 'Planejado', color: '#B45309', bg: '#FEF3C7' },
  { value: 'installed', label: 'Instalado', color: '#1D4ED8', bg: '#DBEAFE' },
  { value: 'osseointegrated', label: 'Osseointegrado', color: '#7C3AED', bg: '#EDE9FE' },
  { value: 'loaded', label: 'Em função', color: '#15803D', bg: '#DCFCE7' },
  { value: 'failed', label: 'Insucesso', color: '#B91C1C', bg: '#FEE2E2' },
  { value: 'removed', label: 'Removido', color: '#475569', bg: '#E2E8F0' },
];

export const ORTHODONTIC_APPLIANCES = [
  'Aparelho fixo metálico',
  'Aparelho fixo estético',
  'Autoligado',
  'Alinhadores',
  'Aparelho móvel',
  'Expansor palatino',
  'Contenção fixa',
  'Contenção removível',
];

export const MALOCCLUSION_CLASSES = [
  { value: 'I', label: 'Classe I' },
  { value: 'II_1', label: 'Classe II divisão 1' },
  { value: 'II_2', label: 'Classe II divisão 2' },
  { value: 'III', label: 'Classe III' },
];

export const ORTHODONTIC_STATUSES = [
  { value: 'planned', label: 'Planejado', color: '#B45309', bg: '#FEF3C7' },
  { value: 'in_progress', label: 'Em andamento', color: '#1D4ED8', bg: '#DBEAFE' },
  { value: 'retention', label: 'Contenção', color: '#7C3AED', bg: '#EDE9FE' },
  { value: 'completed', label: 'Concluído', color: '#15803D', bg: '#DCFCE7' },
  { value: 'interrupted', label: 'Interrompido', color: '#B91C1C', bg: '#FEE2E2' },
];

export function getStatusOption<T extends { value: string; label: string; color: string; bg: string }>(
  options: T[],
  value: string | null | undefined,
): T {
  return options.find((o) => o.value === value) || options[0];
}
